import { createApp } from 'vue';
import { createPinia } from 'pinia';
import App from './App.vue';
import router from './router';
import * as Sentry from '@sentry/vue';

import Aura from '@primevue/themes/aura';
import PrimeVue from 'primevue/config';
import ConfirmationService from 'primevue/confirmationservice';
import ToastService from 'primevue/toastservice';
import { createI18n } from 'vue-i18n';
import en from './locales/en.json';
import fr from './locales/fr.json';
import it from './locales/it.json';
import es from './locales/es.json';
import pt from './locales/pt.json';
import de from './locales/de.json';
import { definePreset } from '@primevue/themes';
import AuthWatcher from './views/AuthWatcher.vue';
import { setLocale } from 'yup';
import { callAPI, DRUPAL_API_ROUTE } from './service/DrupalService';

import '@/assets/styles.scss';
import '@/assets/tailwind.css';
import 'leaflet/dist/leaflet.css';

const sentryEnv = import.meta.env.VITE_SENTRY_ENV;

async function getUserLanguage() {
    var request = await callAPI(DRUPAL_API_ROUTE.USER_LANGUAGE);
    return request.data;
}

getUserLanguage().then((lang) => {
    setLanguage(lang);
    i18n.global.locale.value = lang;
});

const convertJsonToYupLocale = (json) => ({
    mixed: {
        required: json.validation?.required,
        notOneOf: json.validation?.notOneOf
    },
    string: {
        email: json.validation?.email,
        min: json.validation?.min,
        matches: json.validation?.matches
    }
});

const setLanguage = (lang) => {
    switch (lang) {
        case 'fr':
            lang = convertJsonToYupLocale(fr);
            break;
        case 'it':
            lang = convertJsonToYupLocale(it);
            break;
        case 'es':
            lang = convertJsonToYupLocale(es);
            break;
        case 'pt':
            lang = convertJsonToYupLocale(pt);
            break;
        case 'de':
            lang = convertJsonToYupLocale(de);
            break;
        case 'en':
        default:
            lang = convertJsonToYupLocale(en);
            break;
    }
    setLocale(lang);
};

const pinia = createPinia();
const app = createApp(App);
const i18n = createI18n({
    locale: 'en',
    fallbackLocale: 'en',
    messages: { fr, es, it, pt, en, de },
    legacy: false
});

if (JSON.parse(localStorage.getItem('darkMode')) == true) document.documentElement.classList.toggle('app-dark');

Sentry.init({
    environment: sentryEnv,
    app,
    dsn: 'https://501d4c8f2ff0f70565b2787906f3e76d@o4507030412525568.ingest.de.sentry.io/4508122860683344',
    integrations: [Sentry.browserTracingIntegration({ router }), Sentry.replayIntegration()],
    tracesSampleRate: sentryEnv == 'production' ? 0.2 : 1.0,
    tracePropagationTargets: sentryEnv == 'production' ? ['lc.cx/en/api'] : ['localhost'],
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0
});

const Noir = definePreset(Aura, {
    semantic: {
        primary: {
            50: '{sky.50}',
            100: '{sky.100}',
            200: '{sky.200}',
            300: '{sky.300}',
            400: '{sky.400}',
            500: '{sky.500}',
            600: '{sky.600}',
            700: '{sky.700}',
            800: '{sky.800}',
            900: '{sky.900}',
            950: '{sky.950}'
        },
        colorScheme: {
            light: {
                primary: {
                    color: '{sky.950}',
                    inverseColor: '#ffffff',
                    hoverColor: '{sky.900}',
                    activeColor: '{sky.800}'
                },
                highlight: {
                    background: '{sky.950}',
                    focusBackground: '{sky.700}',
                    color: '#ffffff',
                    focusColor: '#ffffff'
                }
            },
            dark: {
                primary: {
                    color: '{sky.50}',
                    inverseColor: '{sky.950}',
                    hoverColor: '{sky.100}',
                    activeColor: '{sky.200}'
                },
                highlight: {
                    background: 'rgba(250, 250, 250, .16)',
                    focusBackground: 'rgba(250, 250, 250, .24)',
                    color: 'rgba(255,255,255,.87)',
                    focusColor: 'rgba(255,255,255,.87)'
                }
            }
        }
    }
});

app.use(pinia);
app.use(router);
app.use(PrimeVue, {
    theme: {
        preset: Noir,
        options: {
            darkModeSelector: '.app-dark'
        }
    }
});
app.use(ToastService);
app.use(ConfirmationService);
app.use(i18n);
app.component('AuthWatcher', AuthWatcher);

app.mount('#app');
